import type { OneToOneAdSetType } from "~/ui-rtk/api/jobsApi"

/**
 * Options and helpers for the "Launch each ad into its own 1:1 ad set" mode
 * of the blast picker (MD-5426). The wire values mirror the backend contract
 * in `OneToOneAdSetsInput`; only the labels/descriptions live here.
 */

export interface OneToOneAdSetTypeOption {
  value: OneToOneAdSetType
  label: string
  description: string
}

/** Audience presets the backend applies to every 1:1 ad set it creates. */
export const ONE_TO_ONE_ADSET_TYPE_OPTIONS: OneToOneAdSetTypeOption[] = [
  {
    value: "LUKEWARM_PROSPECTING",
    label: "LukeWarm Prospecting",
    description: "Excludes Existing Customer audiences",
  },
  {
    value: "COLD_PROSPECTING",
    label: "Cold Prospecting",
    description: "Excludes Engaged and Existing Customer audiences",
  },
  {
    value: "WARM_PROSPECTING",
    label: "Warm Prospecting",
    description: "Targets Engaged audiences, excludes Existing Customers",
  },
  {
    value: "RETENTION",
    label: "Retention",
    description: "Targets Existing Customer audiences",
  },
]

/**
 * Optimization goals a 1:1 ad set can be created with (Meta wire values).
 * Labels follow the same humanized form the AdForge defaults settings use
 * ("Offsite conversions"), with the two Meta brand spellings kept as-is.
 */
export const ONE_TO_ONE_GOAL_OPTIONS: { value: string; label: string }[] = [
  { value: "OFFSITE_CONVERSIONS", label: "Offsite conversions" },
  { value: "VALUE", label: "Value" },
  { value: "LANDING_PAGE_VIEWS", label: "Landing page views" },
  { value: "LINK_CLICKS", label: "Link clicks" },
  { value: "POST_ENGAGEMENT", label: "Post engagement" },
  { value: "THRUPLAY", label: "ThruPlay" },
  { value: "VISIT_INSTAGRAM_PROFILE", label: "Visit Instagram profile" },
  { value: "REACH", label: "Reach" },
]

/**
 * A Profile Visit goal sends people to the Instagram profile, so a social
 * post blasted into such an ad set needs no destination URL (same rule
 * `isProfileVisitAdSet` applies to existing ad sets).
 */
export function isProfileVisitGoal(goal: string): boolean {
  return goal === "VISIT_INSTAGRAM_PROFILE" || goal === "PROFILE_VISIT"
}

/** Parses the daily-budget field; null unless it is a positive finite number. */
export function parseDailyBudget(raw: string): number | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  const value = Number(trimmed)
  return Number.isFinite(value) && value > 0 ? value : null
}

/** Picker state for the 1:1 mode. `dailyBudget` is the raw field text. */
export interface OneToOneAdSetState {
  enabled: boolean
  adSetType: OneToOneAdSetType
  optimizationGoal: string
  dailyBudget: string
}

export const DEFAULT_ONE_TO_ONE: OneToOneAdSetState = {
  enabled: false,
  adSetType: "LUKEWARM_PROSPECTING",
  optimizationGoal: "OFFSITE_CONVERSIONS",
  dailyBudget: "",
}
